import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { Link, navigate } from "../router";
import type { SlotSource, TemplateClause, TemplateFieldIn, TemplateSource } from "../types";
import { FieldPicker } from "./FieldPicker";
import { IconBack, IconBin, IconChevron } from "./Icons";

/**
 * Editing a template: the user's own copy ("Modelele mele"), or - for an
 * admin - one of the shared library. The server decides which (`can_edit`)
 * and checks again on save. A save is a new version: drafts already started
 * keep the wording they were started from, new ones get this.
 *
 * The fields follow the text. A field exists because its {{placeholder}}
 * is in a clause, so there is no "add field" button: write the placeholder,
 * then place it in a category with that category's "+". Until it is placed
 * it waits there, with a red dot. A field whose placeholder is removed from
 * the text is removed with it - the save says so first when that field
 * cites the provision that requires it.
 *
 * Which provision requires a field is not edited here: it is read off the
 * corpus when a template is authored, and carried over by key.
 */

const PLACEHOLDER = /\{\{\s*([\p{L}\p{N}_]+)\s*\}\}/gu;
const NUMERAL = /^\s*[IVXLC]+\.\s*/;
const UNTITLED = "Date generale";
const KINDS: [string, string][] = [
  ["text", "Text"],
  ["date", "Dată"],
  ["amount", "Sumă"],
  ["longtext", "Text lung"],
];

/** Mirrors drafting.section_label: "I. PĂRȚILE" -> "Părțile". */
function sectionLabel(heading: string): string {
  let name = (heading || "").replace(NUMERAL, "").trim();
  if (!name) return UNTITLED;
  if (name === name.toUpperCase() && /\p{L}/u.test(name)) {
    name = name[0] + name.slice(1).toLowerCase();
  }
  return name;
}

/** Mirrors drafting.field_label: "nume_client" -> "Nume client". */
function fieldLabel(key: string): string {
  const words = key.replace(/_/g, " ").trim();
  return words.slice(0, 1).toUpperCase() + words.slice(1);
}

interface Row extends TemplateClause {
  /** React's key; a clause's own key is empty until the server names it. */
  id: number;
}

let nextId = 1;

function rowsOf(src: TemplateSource): Row[] {
  return src.clauses.map((c) => ({ ...c, id: nextId++ }));
}

function fieldsOf(src: TemplateSource): Record<string, TemplateFieldIn> {
  return Object.fromEntries(
    src.slots.map((s) => [
      s.key,
      { label: s.label, kind: s.kind, required: s.required, help_text: s.help_text, section: s.section },
    ]),
  );
}

export function TemplateEditor({ slug }: { slug: string }) {
  const [src, setSrc] = useState<TemplateSource | null>(null);
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [rows, setRows] = useState<Row[]>([]);
  const [fields, setFields] = useState<Record<string, TemplateFieldIn>>({});
  const [changelog, setChangelog] = useState("");
  const [openSec, setOpenSec] = useState<string | null>(null);
  const [picking, setPicking] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const baseline = useRef("");

  function load(s: TemplateSource) {
    const r = rowsOf(s);
    const f = fieldsOf(s);
    setSrc(s);
    setTitle(s.title);
    setSummary(s.summary);
    setRows(r);
    setFields(f);
    setChangelog("");
    baseline.current = JSON.stringify([s.title, s.summary, r.map(({ key, heading, text }) => [key, heading, text]), f]);
  }

  useEffect(() => {
    let live = true;
    api
      .templateSource(slug)
      .then((s) => live && load(s))
      .catch((e) => live && setError(e instanceof Error ? e.message : "Eroare"));
    return () => {
      live = false;
    };
  }, [slug]);

  const sources = useMemo(
    () => Object.fromEntries((src?.slots ?? []).filter((s) => s.source).map((s) => [s.key, s.source!])) as Record<string, SlotSource>,
    [src],
  );
  const known = useMemo(() => new Set((src?.slots ?? []).map((s) => s.key)), [src]);
  const sectionNames = useMemo(() => [...new Set(rows.map((r) => sectionLabel(r.heading)))], [rows]);

  // Every placeholder in the text, in reading order, and the section of the
  // first clause using it - the default for a field the template had.
  const inText = useMemo(() => {
    const first = new Map<string, string>();
    for (const r of rows) {
      for (const m of r.text.matchAll(PLACEHOLDER)) {
        if (!first.has(m[1])) first.set(m[1], sectionLabel(r.heading));
      }
    }
    return first;
  }, [rows]);

  function metaOf(key: string): TemplateFieldIn {
    return fields[key] ?? { label: fieldLabel(key), kind: "text", required: true, help_text: "", section: "" };
  }

  /** Where a field sits: where it was placed; else, for a field the
   *  template already had, its first clause's section; else nowhere yet. */
  function placeOf(key: string): string {
    const chosen = metaOf(key).section;
    if (chosen && sectionNames.includes(chosen)) return chosen;
    return known.has(key) ? inText.get(key) ?? "" : "";
  }

  const placed = new Map<string, string[]>(sectionNames.map((n) => [n, []]));
  for (const key of inText.keys()) {
    const where = placeOf(key);
    if (where) placed.get(where)?.push(key);
  }
  const waiting = [...inText.keys()]
    .filter((k) => !placeOf(k))
    .map((k) => ({ key: k, label: metaOf(k).label }));
  // Fields that cite their provision and that the edit removed from the text.
  const dropping = [...known].filter((k) => !inText.has(k) && sources[k]);

  const dirty =
    !!src &&
    JSON.stringify([title, summary, rows.map(({ key, heading, text }) => [key, heading, text]), fields]) !==
      baseline.current;

  function setMeta(key: string, patch: Partial<TemplateFieldIn>) {
    setFields((f) => ({ ...f, [key]: { ...metaOf(key), ...patch } }));
    setSaved(null);
  }

  function setRow(id: number, patch: Partial<Row>) {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setSaved(null);
  }

  function move(id: number, by: -1 | 1) {
    setRows((rs) => {
      const i = rs.findIndex((r) => r.id === id);
      const j = i + by;
      if (i < 0 || j < 0 || j >= rs.length) return rs;
      const next = rs.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  async function save() {
    if (!src) return;
    setSaving(true);
    setError(null);
    try {
      const next = await api.saveTemplate(src.slug, {
        base_version: src.version,
        title,
        summary,
        clauses: rows.map(({ key, heading, text }) => ({ key, heading, text })),
        fields: Object.fromEntries(
          [...inText.keys()].map((k) => [k, { ...metaOf(k), section: placeOf(k) }]),
        ),
        changelog,
      });
      load(next);
      setSaved(
        next.mine
          ? `Salvat. Documentele începute de acum folosesc versiunea ${next.version}.`
          : `Salvat ca versiunea ${next.version}, pentru toți utilizatorii. ` +
              "Documentele începute de acum o folosesc.",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Eroare");
    } finally {
      setSaving(false);
    }
  }

  const back = (
    <Link to={src?.mine ? "/documents/mine" : "/documents/models"} className="back">
      <IconBack size={18} />
      {src?.mine ? "Modelele mele" : "Modele"}
    </Link>
  );

  async function copyMine() {
    if (!src) return;
    try {
      const made = await api.copyTemplate(src.slug);
      navigate(`/documents/models/${encodeURIComponent(made.slug)}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Eroare");
    }
  }

  if (src && !src.can_edit) {
    return (
      <div className="draft-error">
        {back}
        <p className="muted">
          Modelele comune le modifică doar administratorii. Copiați-l în
          modelele dvs. ca să-l adaptați.
        </p>
        <button className="btn primary" onClick={() => void copyMine()}>
          Copiază în modelele mele
        </button>
        {error && <p className="error" role="alert">{error}</p>}
      </div>
    );
  }
  if (error && !src) {
    return (
      <div className="draft-error">
        {back}
        <p className="error">{error}</p>
      </div>
    );
  }
  if (!src) return <p className="muted draft-loading">se încarcă…</p>;

  return (
    <div className="draft tpl">
      <div className="draft-bar">
        {back}
        <input
          className="title-input"
          value={title}
          aria-label="Denumirea modelului"
          onChange={(e) => {
            setTitle(e.target.value);
            setSaved(null);
          }}
        />
        <span className="badge">versiunea {src.version}</span>
        <button
          className="btn medium primary"
          disabled={!dirty || saving || !title.trim()}
          onClick={() => void save()}
        >
          {saving ? "Se salvează…" : `Salvează versiunea ${src.version + 1}`}
        </button>
      </div>

      <div className="work">
        <section className="pane" aria-label="Câmpuri">
          <div className="pane-body">
            <p className="fineprint tpl-hint">
              Un câmp apare când scrieți {"{{nume}}"} într-o clauză. Puneți-l
              apoi într-o categorie cu „+”.
            </p>
            {sectionNames.map((name) => {
              const open = openSec === name;
              const keys = placed.get(name) ?? [];
              return (
                <div className={open ? "sec open" : "sec"} key={name}>
                  <div className="sec-top">
                    <button
                      className="sec-head"
                      aria-expanded={open}
                      onClick={() => setOpenSec(open ? null : name)}
                    >
                      {name}
                      <span className="sec-st">
                        {keys.length === 1 ? "1 câmp" : `${keys.length} câmpuri`}
                      </span>
                    </button>
                    <FieldPicker
                      section={name}
                      waiting={waiting}
                      open={picking === name}
                      onToggle={(o) => setPicking(o ? name : null)}
                      onPick={(key) => {
                        setMeta(key, { section: name });
                        setOpenSec(name);
                      }}
                    />
                  </div>
                  {open && (
                    <div className="sec-body">
                      {keys.length === 0 && <p className="fineprint">Niciun câmp în această categorie.</p>}
                      {keys.map((key) => (
                        <FieldSpec
                          key={key}
                          name={key}
                          meta={metaOf(key)}
                          source={sources[key]}
                          onChange={(patch) => setMeta(key, patch)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <section className="paper-wrap tpl-body" aria-label="Clauze">
          <div className="tpl-col">
            <label className="tpl-label" htmlFor="tpl-summary">Descriere</label>
            <textarea
              id="tpl-summary"
              className="tpl-summary"
              rows={2}
              value={summary}
              onChange={(e) => {
                setSummary(e.target.value);
                setSaved(null);
              }}
            />

            {rows.map((r, i) => (
              <div className="tpl-clause" key={r.id}>
                <div className="tpl-clause-top">
                  <input
                    className="tpl-heading"
                    value={r.heading}
                    placeholder="Titlul secțiunii (opțional)"
                    aria-label={`Titlul clauzei ${i + 1}`}
                    onChange={(e) => setRow(r.id, { heading: e.target.value })}
                  />
                  <button className="iconb" title="Mută mai sus" aria-label="Mută mai sus"
                          disabled={i === 0} onClick={() => move(r.id, -1)}>
                    <span className="up"><IconChevron size={16} /></span>
                  </button>
                  <button className="iconb" title="Mută mai jos" aria-label="Mută mai jos"
                          disabled={i === rows.length - 1} onClick={() => move(r.id, 1)}>
                    <span className="down"><IconChevron size={16} /></span>
                  </button>
                  <button className="iconb" title="Șterge clauza" aria-label="Șterge clauza"
                          onClick={() => setRows((rs) => rs.filter((x) => x.id !== r.id))}>
                    <IconBin size={16} />
                  </button>
                </div>
                <textarea
                  className="tpl-text"
                  rows={Math.max(3, r.text.split("\n").length + 1)}
                  value={r.text}
                  aria-label={`Textul clauzei ${i + 1}`}
                  onChange={(e) => setRow(r.id, { text: e.target.value })}
                />
              </div>
            ))}
            <button
              className="btn medium"
              onClick={() => setRows((rs) => [...rs, { id: nextId++, key: "", heading: "", text: "" }])}
            >
              Adaugă o clauză
            </button>

            <div className="tpl-save">
              <label className="tpl-label" htmlFor="tpl-log">Ce s-a schimbat (opțional)</label>
              <input
                id="tpl-log"
                className="tpl-heading"
                value={changelog}
                onChange={(e) => setChangelog(e.target.value)}
              />
              {waiting.length > 0 && (
                <p className="field-warn">
                  {waiting.length === 1 ? "Un câmp nu are" : `${waiting.length} câmpuri nu au`} categorie
                  — la salvare intră în secțiunea primei clauze care le folosește.
                </p>
              )}
              {dropping.length > 0 && (
                <p className="field-warn">
                  Nu mai apar în text și vor fi eliminate, deși legea le cere:{" "}
                  {dropping.map((k) => `${metaOf(k).label} (${sources[k].note})`).join(", ")}.
                </p>
              )}
              {error && <p className="error" role="alert">{error}</p>}
              {saved && <p className="ok-note">{saved}</p>}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function FieldSpec({
  name,
  meta,
  source,
  onChange,
}: {
  name: string;
  meta: TemplateFieldIn;
  source?: SlotSource;
  onChange: (patch: Partial<TemplateFieldIn>) => void;
}) {
  return (
    <div className="field tpl-field">
      <div className="field-label">
        <code className="tpl-key">{`{{${name}}}`}</code>
        {source && <span className="art" title="Prevederea care cere câmpul">{source.note}</span>}
      </div>
      <input
        value={meta.label}
        aria-label={`Eticheta câmpului ${name}`}
        placeholder="Eticheta"
        onChange={(e) => onChange({ label: e.target.value })}
      />
      <div className="tpl-field-row">
        <select
          value={meta.kind}
          aria-label={`Tipul câmpului ${name}`}
          onChange={(e) => onChange({ kind: e.target.value })}
        >
          {KINDS.map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <label className="tpl-check">
          <input
            type="checkbox"
            checked={meta.required}
            onChange={(e) => onChange({ required: e.target.checked })}
          />
          Obligatoriu
        </label>
      </div>
      <input
        value={meta.help_text}
        aria-label={`Indicația câmpului ${name}`}
        placeholder="Indicație (opțional)"
        onChange={(e) => onChange({ help_text: e.target.value })}
      />
    </div>
  );
}
